import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Loader, Tabs } from '@itds/elements/index.js';

import Config from '@/config';

import QuoteActions from './QuoteActions';
import General from './General';
import LimitCarriers from './LimitCarriers';
import LimitProductTypes from './LimitProductTypes';
import ThemeOptions from './ThemeOptions';
import EmbedCode from './EmbedCode';

import LimitStates from '@brokerage/components/LimitStates';

import { Api } from '@brokerage/utils/Api';

const Show = ({ detail, token, controlledTabs, controlledTabKey, onTabChange, onParentSave }) => {
  const params = useParams();

  const [annuity_quoter, setAnnuityQuoter] = useState(detail || {});
  const [error_message, setErrorMessage] = useState();
  const [success_message, setSuccessMessage] = useState();
  const [loading, setLoading] = useState(!detail);
  // Set default tab: 'general' for standalone, 'product_defaults' for controlled
  const [activeTab, setActiveTab] = useState(
    controlledTabKey || (controlledTabs ? 'product_defaults' : 'general')
  );

  useEffect(() => {
    if (!detail) {
      loadAnnuityQuoter();
    }
  }, [params.id, detail]);

  // Add this effect to update state when detail changes in controlled mode
  useEffect(() => {
    if (controlledTabs && detail) {
      setAnnuityQuoter(detail);
    }
  }, [controlledTabs, detail]);

  useEffect(() => {
    if (controlledTabKey) setActiveTab(controlledTabKey);
  }, [controlledTabKey]);

  useEffect(() => {
    return () => {
      setAnnuityQuoter({});
    };
  }, []);

  const loadAnnuityQuoter = () => {
    Api.loadAnnuityQuoter(params.id).then(({ data }) => {
      setAnnuityQuoter(data);
      setLoading(false);
    });
  };

  const handleStatesChange = ({ default_state, states }) => {
    handleSave({ default_state, states });
  };

  const handleSave = input_fields => {
    window.scrollTo(0, 0);
    // If in controlled mode, call parent save handler
    if (controlledTabs && typeof onParentSave === 'function') {
      onParentSave(input_fields);
      return;
    }
    if (detail) {
      return;
    }
    Api.updateAnnuityQuoter(params.id, input_fields)
      .then(() => {
        loadAnnuityQuoter();
        setErrorMessage(null);
        setSuccessMessage('Annuity Quoter updated successfully');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setSuccessMessage(null);
        setErrorMessage('Unable to update Annuity Quoter');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  // Tabs for controlled (Universal Quoter) mode
  const uqTabs = [
    { label: 'Product Defaults', key: 'product_defaults' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' }
  ];
  // Tabs for standalone mode
  const standaloneTabs = [
    { label: 'General', key: 'general' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' },
    { label: 'Preview', key: 'preview' },
    { label: 'Embed Code', key: 'embed_code' }
  ];
  const tabs = controlledTabs ? uqTabs : standaloneTabs;

  // Map tab key to component
  const isControlled = !!controlledTabs;
  const quoterData = isControlled ? detail : annuity_quoter;
  const child_props = {
    annuity_quoter: quoterData,
    handleSave,
    loadAnnuityQuoter,
    setAnnuityQuoter,
    setErrorMessage,
    setSuccessMessage
  };
  const tabContent = {
    product_defaults: <General {...child_props} />, // Universal Quoter mode
    general: <General {...child_props} />, // Standalone mode
    limit_carriers: <LimitCarriers {...child_props} annuity_quoter={quoterData} />, // use latest data
    limit_product_types: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_products: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_states: (
      <LimitStates
        defaultState={quoterData.default_state}
        handleSave={handleStatesChange}
        selectedStates={quoterData.states}
      />
    ),
    custom_actions: <QuoteActions {...child_props} />,
    theme_options: <ThemeOptions {...child_props} />,
    preview: null,
    embed_code: !controlledTabs ? <EmbedCode {...child_props} /> : null
  };

  // Handle Preview tab in standalone mode
  const handleTabClick = tabKey => {
    if (!controlledTabs && tabKey === 'preview') {
      window.open(`${Config.AQ_URL}?token=${token || annuity_quoter.token}`);
      setActiveTab('general');
      if (onTabChange) onTabChange('general');
      return;
    }
    setActiveTab(tabKey);
    if (onTabChange) onTabChange(tabKey);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}

      {/* Only render tabs and layout if not controlled by Universal Quoter */}
      {!controlledTabs ? (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tabs
            tabs={tabs.map(tab => ({
              label: tab.label,
              to: tab.key,
              isActive: activeTab === tab.key,
              onClick: () => handleTabClick(tab.key)
            }))}
            vertical={true}
          />
          <div style={{ flex: 1, marginLeft: 40 }}>{tabContent[activeTab]}</div>
        </div>
      ) : (
        // In controlled mode, render ONLY the active tab content, no wrappers
        <>{tabContent[activeTab]}</>
      )}
    </>
  );
};

Show.propTypes = {
  controlledTabKey: PropTypes.string,
  controlledTabs: PropTypes.array,
  detail: PropTypes.object,
  onParentSave: PropTypes.func,
  onTabChange: PropTypes.func,
  token: PropTypes.string
};

Show.General = General;
export default Show;
